import React, { useEffect, useId, useRef, useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";

type MermaidDiagramProps = {
  chart: string;
};

/**
 * Renders a Mermaid chart client-side. Used for University lesson diagrams that were
 * imported as plain `[mermaid]\\n...` Portable Text blocks.
 */
export function MermaidDiagram({ chart }: MermaidDiagramProps): React.ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const reactId = useId().replace(/:/g, "");
  const [error, setError] = useState<string | null>(null);
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const bg = useColorModeValue("gray.50", "whiteAlpha.50");
  const errorColor = useColorModeValue("red.600", "red.300");
  const theme = useColorModeValue("neutral", "dark");

  useEffect(() => {
    let cancelled = false;
    const source = chart.trim();
    if (!source) {
      setError("Empty diagram");
      return undefined;
    }

    const render = async () => {
      setError(null);
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme,
          fontFamily: "IBM Plex Sans, sans-serif",
        });

        const renderId = `mermaid-${reactId}-${Math.random().toString(36).slice(2, 9)}`;
        const { svg } = await mermaid.render(renderId, source);
        if (cancelled) return;
        if (hostRef.current) {
          hostRef.current.innerHTML = svg;
          const svgEl = hostRef.current.querySelector("svg");
          if (svgEl) {
            svgEl.setAttribute("width", "100%");
            svgEl.style.maxWidth = "100%";
            svgEl.style.height = "auto";
          }
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to render diagram";
        setError(message);
        if (hostRef.current) hostRef.current.innerHTML = "";
      }
    };

    void render();
    return () => {
      cancelled = true;
    };
  }, [chart, reactId, theme]);

  return (
    <Box
      data-university-mermaid=""
      my="1.25rem"
      p="1rem"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="6px"
      bg={bg}
      overflowX="auto"
    >
      {error ? (
        <Text fontSize="sm" color={errorColor} fontFamily="'IBM Plex Sans', sans-serif">
          Could not render diagram. {error}
        </Text>
      ) : null}
      <Box ref={hostRef} aria-hidden={Boolean(error)} />
    </Box>
  );
}

/** Extract Mermaid source from an imported plain-text / code Portable Text block. */
export function extractMermaidSourceFromBlock(block: unknown): string | null {
  if (!block || typeof block !== "object") return null;
  const children = (block as { children?: unknown }).children;
  if (!Array.isArray(children)) return null;

  const text = children
    .map((child) =>
      child && typeof child === "object" && typeof (child as { text?: unknown }).text === "string"
        ? (child as { text: string }).text
        : "",
    )
    .join("")
    .trim();

  if (!text) return null;

  const labeled = text.match(/^\[mermaid\]\s*\n?([\s\S]+)$/i);
  if (labeled?.[1]?.trim()) return labeled[1].trim();

  // Bare Mermaid (no [mermaid] label) that starts with a known diagram keyword.
  if (
    /^(flowchart|graph|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline)\b/i.test(
      text,
    )
  ) {
    return text;
  }

  return null;
}
